import { html, nothing, type TemplateResult } from "lit";

export type LxSelectOption = {
  value: string;
  label: string;
  desc?: string;
};

let activePanel: HTMLElement | null = null;

function closeActivePanel() {
  if (activePanel) {
    activePanel.remove();
    activePanel = null;
  }
}

// 全局点击关闭
let globalListenerAdded = false;
function ensureGlobalListener() {
  if (globalListenerAdded) return;
  globalListenerAdded = true;
  document.addEventListener("pointerdown", (e) => {
    if (activePanel && !activePanel.contains(e.target as Node)) {
      // 检查是否点击了触发按钮
      const trigger = (e.target as HTMLElement).closest(".lx-dropdown");
      if (!trigger) closeActivePanel();
    }
  });
}

function showPanel(trigger: HTMLElement, options: LxSelectOption[], value: string, onChange: (v: string) => void, requestUpdate: () => void) {
  closeActivePanel();

  const rect = trigger.getBoundingClientRect();
  const panel = document.createElement("div");
  panel.className = "lx-dropdown-portal";
  panel.style.cssText = `
    position:fixed; z-index:9999;
    left:${rect.left}px;
    bottom:${window.innerHeight - rect.top + 4}px;
    min-width:${Math.max(rect.width, 160)}px;
    max-width:320px; max-height:280px;
    background:var(--card, #fff);
    border:1px solid var(--border, #e5e0d8);
    border-radius:10px;
    box-shadow:0 -4px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06);
    padding:4px;
    overflow-y:auto;
    animation:lx-dropdown-in 0.15s ease;
  `;

  for (const opt of options) {
    const item = document.createElement("div");
    const isActive = opt.value === value;
    item.style.cssText = `
      display:flex; align-items:center; justify-content:space-between; gap:12px;
      padding:8px 12px; border-radius:6px; font-size:13px; cursor:pointer;
      transition:background 0.1s;
      ${isActive ? "background:rgba(217,119,6,0.06); color:#D97706; font-weight:600;" : "color:var(--text, #1A1915);"}
    `;
    item.addEventListener("mouseenter", () => { if (!isActive) item.style.background = "var(--bg-hover, #f5f3ef)"; });
    item.addEventListener("mouseleave", () => { if (!isActive) item.style.background = ""; });

    const labelSpan = document.createElement("span");
    labelSpan.style.cssText = "flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;";
    labelSpan.textContent = opt.label;
    item.appendChild(labelSpan);

    if (opt.desc) {
      const descSpan = document.createElement("span");
      descSpan.style.cssText = "font-size:11px; color:var(--muted, #9C9589); white-space:nowrap; flex-shrink:0;";
      descSpan.textContent = opt.desc;
      item.appendChild(descSpan);
    }

    item.addEventListener("click", (e) => {
      e.stopPropagation();
      onChange(opt.value);
      closeActivePanel();
      requestUpdate();
    });

    panel.appendChild(item);
  }

  document.body.appendChild(panel);
  activePanel = panel;
}

export function renderLxSelect(opts: {
  options: LxSelectOption[];
  value: string;
  placeholder?: string;
  disabled?: boolean;
  mini?: boolean;
  onChange: (value: string) => void;
  requestUpdate: () => void;
}): TemplateResult {
  ensureGlobalListener();
  const selected = opts.options.find((o) => o.value === opts.value);
  const displayLabel = selected?.label || opts.placeholder || "请选择";
  const cls = `lx-dropdown${opts.mini ? " lx-dropdown--mini" : ""}${opts.disabled ? " lx-dropdown--disabled" : ""}`;

  return html`
    <div class="${cls}"
      @click=${(e: Event) => {
        if (opts.disabled) return;
        e.stopPropagation();
        const el = (e.currentTarget as HTMLElement);
        const trigger = el.querySelector(".lx-dropdown__trigger") as HTMLElement;
        if (activePanel) {
          closeActivePanel();
        } else {
          showPanel(trigger, opts.options, opts.value, opts.onChange, opts.requestUpdate);
        }
      }}
    >
      <button type="button" class="lx-dropdown__trigger" ?disabled=${opts.disabled}>
        <span class="lx-dropdown__label">${displayLabel}</span>
        <svg class="lx-dropdown__arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>
      </button>
    </div>
  `;
}
